import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import Radio from "antd/es/radio";
import Slider from "antd/es/slider";
import { useIntl } from "react-intl";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import { NumberField } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import SelectField from "@app/component/input/SelectField";
import Sidebar from "@app/scene/map/components/sidebar/Sidebar";
import { updateDemographicDataConfiguration } from "@app/scene/map/store/actions";
import { getDemographicDataConfiguration } from "@app/scene/map/store/layers/boundaries/selectors";
import { DemographicDataConfiguration } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import ClassificationMethod from "@app/scene/map/store/layers/boundaries/types/ClassificationMethod";
import DataSource from "@app/scene/map/store/layers/boundaries/types/DataSource";
import colorPalette from "@app/scene/map/store/layers/boundaries/util/colorPalette";
import { RootState } from "@app/store/rootReducer";
import formatCountryName from "@app/util/countries/formatCountryName";

import styles from "./DemographyPanel.module.scss";
import getClassificationMethodDisplayName from "./getClassificationMethodDisplayName";
import getDataSourceDisplayName from "./getDataSourceDisplayName";
import { messages } from "./messages";
import Theme from "./Theme";

const MIN_BUCKETS = 2;
const MAX_BUCKETS = 9;

interface Props {
  demographicDataConfiguration: DemographicDataConfiguration;
  onHide: () => void;
  onUpdateDemographicDataConfiguration: typeof updateDemographicDataConfiguration;
}

const DemographyPanel: React.FC<Props> = ({
  demographicDataConfiguration,
  onHide,
  onUpdateDemographicDataConfiguration,
}) => {
  const intl = useIntl();

  const { bucketCount, classificationMethod, countryCode, dataSource, onlyVisibleRegions, theme } =
    demographicDataConfiguration;
  const themeValue = colorPalette.findIndex(
    (palette) => palette.startColor === theme.startColor && palette.endColor === theme.endColor
  );

  const handleCountryChange = useCallback(
    (value: CountryCode) => {
      onUpdateDemographicDataConfiguration({ countryCode: value });
    },
    [onUpdateDemographicDataConfiguration]
  );

  const handleSourceChange = useCallback(
    (value: DataSource) => {
      onUpdateDemographicDataConfiguration({ dataSource: value });
    },
    [onUpdateDemographicDataConfiguration]
  );

  const handleMethodChange = useCallback(
    (value: ClassificationMethod) => {
      onUpdateDemographicDataConfiguration({ classificationMethod: value });
    },
    [onUpdateDemographicDataConfiguration]
  );

  const handleColorByChange = useCallback(
    (value: boolean) => {
      onUpdateDemographicDataConfiguration({ onlyVisibleRegions: value });
    },
    [onUpdateDemographicDataConfiguration]
  );

  const handleBucketsChange = useCallback(
    (value: number | undefined) => {
      if (value !== undefined) {
        onUpdateDemographicDataConfiguration({ bucketCount: value });
      }
    },
    [onUpdateDemographicDataConfiguration]
  );

  const handleThemeChange = useCallback(
    (value: number) => {
      onUpdateDemographicDataConfiguration({ theme: colorPalette[value] });
    },
    [onUpdateDemographicDataConfiguration]
  );

  const sourceOptions = useMemo(
    () => [
      {
        label: intl.formatMessage(messages[DataSource.POPULATION], {
          dataSource: getDataSourceDisplayName(intl, countryCode),
        }),
        value: DataSource.POPULATION,
      },
      {
        label: intl.formatMessage(messages[DataSource.MEDIAN_INCOME], {
          dataSource: getDataSourceDisplayName(intl, countryCode),
        }),
        value: DataSource.MEDIAN_INCOME,
      },
    ],
    [countryCode, intl]
  );

  const countryOptions = useMemo(
    () => [
      {
        label: formatCountryName(intl, CountryCode["CANADA"]),
        value: CountryCode["CANADA"],
      },
      {
        label: formatCountryName(intl, CountryCode["UNITED STATES OF AMERICA"]),
        value: CountryCode["UNITED STATES OF AMERICA"],
      },
    ],
    [intl]
  );

  return (
    <Sidebar mask={false} onClose={onHide} title={intl.formatMessage(messages.title)} visible>
      <div className={styles.container}>
        <SelectField
          className={styles.inputContainer}
          label={intl.formatMessage(messages.country)}
          onChange={handleCountryChange}
          options={countryOptions}
          value={countryCode}
        />
        <SelectField
          className={styles.inputContainer}
          label={intl.formatMessage(messages.dataSource)}
          onChange={handleSourceChange}
          options={sourceOptions}
          value={dataSource}
        />
        <div className={styles.preferences}>
          <span className={styles.title}>{intl.formatMessage(messages.preferences)}</span>
          <div className={styles.inputContainer}>
            <span className={styles.label}>
              {intl.formatMessage(messages.classificationMethod)}
            </span>
            <RadioGroup<ClassificationMethod>
              onChange={handleMethodChange}
              value={classificationMethod}
            >
              <Radio.Button
                key={ClassificationMethod.NATURAL_BREAKS}
                value={ClassificationMethod.NATURAL_BREAKS}
              >
                {getClassificationMethodDisplayName(intl, ClassificationMethod.NATURAL_BREAKS)}
              </Radio.Button>
              <Radio.Button
                key={ClassificationMethod.QUANTILE}
                value={ClassificationMethod.QUANTILE}
              >
                {getClassificationMethodDisplayName(intl, ClassificationMethod.QUANTILE)}
              </Radio.Button>
              <Radio.Button
                key={ClassificationMethod.EQUAL_INTERVAL}
                value={ClassificationMethod.EQUAL_INTERVAL}
              >
                {getClassificationMethodDisplayName(intl, ClassificationMethod.EQUAL_INTERVAL)}
              </Radio.Button>
            </RadioGroup>
            <span className={styles.description}>
              {intl.formatMessage(messages[classificationMethod])}
            </span>
          </div>
          <div className={styles.inputContainer}>
            <span className={styles.label}>{intl.formatMessage(messages.colorBy)}</span>
            <RadioGroup onChange={handleColorByChange} value={onlyVisibleRegions}>
              <Radio.Button value={false}>{intl.formatMessage(messages.entire)}</Radio.Button>
              <Radio.Button value={true}>{intl.formatMessage(messages.onlyVisible)}</Radio.Button>
            </RadioGroup>
            <span className={styles.description}>
              {intl.formatMessage(
                onlyVisibleRegions
                  ? messages.whatsVisibleDescription
                  : messages.entireSetDescription
              )}
            </span>
          </div>
          <div>
            <span className={styles.label}>{intl.formatMessage(messages.buckets)}</span>
            <Row justify="space-between">
              <Col span={18}>
                <Slider
                  className={styles.slider}
                  max={MAX_BUCKETS}
                  min={MIN_BUCKETS}
                  onChange={handleBucketsChange}
                  value={bucketCount}
                />
              </Col>
              <Col span={5}>
                <NumberField
                  max={MAX_BUCKETS}
                  min={MIN_BUCKETS}
                  onChange={handleBucketsChange}
                  size="middle"
                  value={bucketCount}
                />
              </Col>
            </Row>
          </div>
          <div>
            <span className={styles.label}>{intl.formatMessage(messages.theme)}</span>
            <RadioGroup
              className={styles.themeGroup}
              onChange={handleThemeChange}
              value={themeValue}
            >
              {colorPalette.map((theme, index) => (
                <Radio className={styles.customRadio} key={index} value={index}>
                  <Theme endColor={theme.endColor} startColor={theme.startColor} />
                </Radio>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  demographicDataConfiguration: getDemographicDataConfiguration(state),
});

const mapDispatchToProps = {
  onUpdateDemographicDataConfiguration: updateDemographicDataConfiguration,
};

export default connect(mapStateToProps, mapDispatchToProps)(DemographyPanel);
